<script>
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { fade } from "svelte/transition";
import { onMount, onDestroy } from "svelte";
import { v4 as uuidv4 } from "uuid";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";
import * as m from "@app/paraglide/messages.js";
import { assignEventAudio, clearEventAudio } from "@app/lib/audio/assignEventAudio.js";

/* Firebase */
import { auth, functions } from "@app/configs/firebase.js";
import storage from "@app/configs/storage.js";
import { ref, uploadBytesResumable } from "firebase/storage";
import { httpsCallable } from "firebase/functions";

let wavesurfer = null;
let recordPlugin = null;
let isRecording = $state(false);
let isProcessing = $state(false);
let recordedBlob = $state(null);
let preRoll = $state(2); // Default 2 seconds
let eventIndex = eventGroupState[projectState.selected].selected[0];
let event = eventGroupState[projectState.selected].events[eventIndex];

let uploadProgress = $state(0);
let preRollCountdown = $state(null);
let preRollTimeout = $state(null);

let originalAudioFile = event.audioFile;
let originalAudioFileDuration = event.audioFileDuration;
let originalPeaksData = Array.isArray(event.peaksData) ? event.peaksData : [];
let originalPeaksSampleRate = event.peaksSampleRate || 0;
clearEventAudio(event);

// Teleprompter state - Load from localStorage or use defaults
let scrollSpeed = $state(
    (() => {
        try {
            const saved = localStorage.getItem("teleprompterScrollSpeed");
            const value = saved ? parseInt(saved, 10) : 50;
            // Clamp to valid range (10-150)
            return Math.max(10, Math.min(150, value));
        } catch {
            return 50;
        }
    })(),
); // Pixels per second (default medium speed)
let fontSize = $state(
    (() => {
        try {
            const saved = localStorage.getItem("teleprompterFontSize");
            const value = saved ? parseInt(saved, 10) : 24;
            // Clamp to valid range (12-72)
            return Math.max(12, Math.min(72, value));
        } catch {
            return 24;
        }
    })(),
); // Font size in pixels
let teleprompterElement = $state(null);
let scrollAnimationFrame = $state(null);
let lastScrollTime = $state(null);
let prompterText = event && event.text ? convertToPlainText(event.text) : "";
let autoScrollSpeed = $state(false); // Toggle for auto-calculating scroll speed
let teleprompterReady = $state(false); // Track when teleprompter is measured

// Countdown state
let remainingTime = $state(0);
let countdownInterval = $state(null);

// Audio device state
let audioDevices = $state([]);
let selectedDeviceId = $state(
    (() => {
        try {
            return localStorage.getItem("audioRecorderDeviceId") || null;
        } catch {
            return null;
        }
    })(),
);

// Draggable state
let isDragging = $state(false);
let dragStartX = $state(0);
let dragStartY = $state(0);
let modalX = $state(0);
let modalY = $state(0);
let modalElement = $state(null);

// Derived values
let duration = $derived.by(() => {
    if (!event || event.start === false || event.end === false) return 0;
    return event.end - event.start;
});

let maxPreRoll = $derived.by(() => {
    if (!event || event.start === false) return 15;
    // Max pre-roll is the lesser of 15 seconds or the event start time
    return Math.min(15, event.start);
});

let canRecord = $derived.by(() => {
    if (!event) return false;
    if (event.start === false || event.end === false) return false;
    if (duration < 2) return false;
    if (duration > 30) return false;
    return true;
});

let fontFamily = $derived.by(() => {
    if (!eventGroupState[projectState.selected]) return "Arial, sans-serif";
    return (
        eventGroupState[projectState.selected].fontFamily || "Arial, sans-serif"
    );
});

let countdownProgress = $derived.by(() => {
    if (duration === 0) return 0;
    return (remainingTime / duration) * 100;
});

// Calculate optimal scroll speed based on content and duration
let calculatedScrollSpeed = $state(50);

$effect(() => {
    // Track dependencies to ensure recalculation on layout changes
    const _fs = fontSize;
    const _txt = prompterText;
    const _dur = duration;
    const _el = teleprompterElement;
    const _ready = teleprompterReady;

    if (!_el || _dur === 0) {
        return;
    }
    
    // Measure DOM
    const scrollHeight = _el.scrollHeight;
    const clientHeight = _el.clientHeight;
    const maxScroll = Math.max(0, scrollHeight - clientHeight);
    
    if (maxScroll === 0) {
        calculatedScrollSpeed = 50; // Default fallback
        return;
    }
    
    // Calculate pixels per second needed to finish scrolling at end of recording
    // Use 1.2 multiplier to ensure the text moves up fast enough for reading
    const optimalSpeed = (maxScroll / _dur) * 1.2;
    
    // Clamp to valid range (10-150)
    calculatedScrollSpeed = Math.round(optimalSpeed);
});

// Use auto-calculated speed if enabled, otherwise use manual setting
let effectiveScrollSpeed = $derived(autoScrollSpeed ? calculatedScrollSpeed : scrollSpeed);

// Clamp pre-roll to maxPreRoll when event changes
$effect(() => {
    if (preRoll > maxPreRoll) {
        preRoll = maxPreRoll;
    }
});

// Persist scroll speed to localStorage
$effect(() => {
    try {
        localStorage.setItem("teleprompterScrollSpeed", scrollSpeed.toString());
    } catch (err) {
        console.error("Error saving scroll speed:", err);
    }
});

// Persist font size to localStorage
$effect(() => {
    try {
        localStorage.setItem("teleprompterFontSize", fontSize.toString());
    } catch (err) {
        console.error("Error saving font size:", err);
    }
});

// Persist selected audio device to localStorage
$effect(() => {
    if (selectedDeviceId) {
        try {
            localStorage.setItem("audioRecorderDeviceId", selectedDeviceId);
        } catch (err) {
            console.error("Error saving audio device selection:", err);
        }
    }
});

// Mark teleprompter as ready when element is available and has content
$effect(() => {
    if (teleprompterElement && prompterText) {
        // Wait a tick for the DOM to fully render
        setTimeout(() => {
            teleprompterReady = true;
        }, 0);
    } else {
        teleprompterReady = false;
    }
});

onMount(() => {
    initWavesurfer();
    loadAudioDevices();
    loadSavedModalPosition();
    // Add event listeners for dragging
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
});

onDestroy(() => {
    cleanup();
    // Remove event listeners
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
});

function loadSavedModalPosition() {
    // Load saved position from localStorage or reset to center
    try {
        const savedPosition = localStorage.getItem("audioRecorderPosition");
        if (savedPosition) {
            const { x, y } = JSON.parse(savedPosition);
            modalX = x;
            modalY = y;
        } else {
            modalX = 0;
            modalY = 0;
        }
    } catch (err) {
        console.error("Error loading modal position:", err);
        modalX = 0;
        modalY = 0;
    }
}

function initWavesurfer() {
    try {
        const container = document.querySelector("#audioRecorderWaveform");
        if (!container) return;

        wavesurfer = WaveSurfer.create({
            container: container,
            waveColor: "#0fbc8c",
            progressColor: "#0de9aa",
            cursorColor: "#ffc107",
            barWidth: 2,
            barGap: 1,
            height: 100
        });

        recordPlugin = wavesurfer.registerPlugin(
            RecordPlugin.create({
                scrollingWaveform: false,
                continuousWaveform: true,
                continuousWaveformDuration: event.end - event.start,
                renderRecordedAudio: false,
                audioBitsPerSecond: 128000,
                mimeType: "audio/webm",
            }),
        );

        recordPlugin.on("record-end", (blob) => {
            recordedBlob = blob;
            isRecording = false;
            if (preRollTimeout) {
                clearTimeout(preRollTimeout);
                preRollTimeout = null;
            }
            preRollCountdown = null;
        });

        recordPlugin.on("record-progress", (time) => {
            // Check if we've reached the end time
            if (event && player && duration > 0) {
                const currentTime = player.currentTime;
                if (currentTime >= event.end) {
                    stopRecording();
                }
            }
        });
    } catch (err) {
        console.error("Error initializing wavesurfer:", err);
        toast.push(m.audio_recorder_init_failed({ error: err.message }), {
            classes: ["toast-danger"],
        });
    }
}

async function loadAudioDevices() {
    try {
        var constraints = {
            audio: {
                echoCancellation: false,
                noiseSuppression: false, // Optional: also disable noise suppression
                autoGainControl: false,  // Optional: also disable auto gain control
            },
            video : false
        };

        // Request microphone permission first
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        // Stop the stream immediately after getting permission
        stream.getTracks().forEach((track) => track.stop());

        // Get list of audio input devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        audioDevices = devices.filter((device) => device.kind === "audioinput");

        // console.log("Available audio devices:", $state.snapshot(audioDevices));

        // Check if saved device is still available
        const savedDeviceExists =
            selectedDeviceId &&
            audioDevices.some((device) => device.deviceId === selectedDeviceId);

        // Set default device if not already set or if saved device is no longer available
        if (
            audioDevices.length > 0 &&
            (!selectedDeviceId || !savedDeviceExists)
        ) {
            selectedDeviceId = audioDevices[0].deviceId;
        }
    } catch (err) {
        console.error("Error loading audio devices:", err);
        toast.push(m.audio_recorder_load_devices_failed({ error: err.message }), {
            classes: ["toast-warning"],
        });
    }
}

async function handleDeviceChange() {
    // Reinitialize the record plugin with the new device
    if (recordPlugin && selectedDeviceId) {
        try {
            // Stop any existing recording
            if (recordPlugin.isRecording()) {
                recordPlugin.stopRecording();
            }

            // Destroy and recreate the plugin with new device
            recordPlugin.destroy();

            recordPlugin = wavesurfer.registerPlugin(
                RecordPlugin.create({
                    scrollingWaveform: true,
                    continuousWaveform: false,
                    renderRecordedAudio: true,
                    deviceId: selectedDeviceId,
                }),
            );

            recordPlugin.on("record-end", (blob) => {
                recordedBlob = blob;
                isRecording = false;
                if (preRollTimeout) {
                    clearTimeout(preRollTimeout);
                    preRollTimeout = null;
                }
                preRollCountdown = null;
            });

            recordPlugin.on("record-progress", (time) => {
                if (event && player && duration > 0) {
                    const currentTime = player.currentTime;
                    if (currentTime >= event.end) {
                        stopRecording();
                    }
                }
            });

            toast.push(m.audio_recorder_device_changed(), {
                classes: ["toast-success"],
            });
        } catch (err) {
            console.error("Error changing audio device:", err);
            toast.push(m.audio_recorder_device_change_failed({ error: err.message }), {
                classes: ["toast-danger"],
            });
        }
    }
}

function cleanup() {
    if (preRollTimeout) {
        clearTimeout(preRollTimeout);
        preRollTimeout = null;
    }
    if (scrollAnimationFrame) {
        cancelAnimationFrame(scrollAnimationFrame);
        scrollAnimationFrame = null;
    }
    if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
    }
    if (recordPlugin) {
        recordPlugin.stopRecording();
    }
    if (wavesurfer) {
        wavesurfer.destroy();
    }
    recordedBlob = null;
    preRollCountdown = null;
    remainingTime = 0;
    lastScrollTime = null;
    // Reset teleprompter scroll
    if (teleprompterElement) {
        teleprompterElement.scrollTop = 0;
    }

    // Restore original audio + peaks if recording was not saved
    if (!event.audioFile) {
        event.audioFile = originalAudioFile;
        event.audioFileDuration = originalAudioFileDuration;
        event.peaksData = originalPeaksData;
        event.peaksSampleRate = originalPeaksSampleRate;
    }
}

async function startRecording() {
    if (!canRecord) {
        toast.push(m.audio_recorder_event_duration_invalid(), {
            classes: ["toast-warning"],
        });
        return;
    }

    try {
        // Reset any previous recording
        recordedBlob = null;

        // Set player position to start time minus pre-roll
        const startPosition = Math.max(0, event.start - preRoll);
        if (player) {
            player.currentTime = startPosition;
            player.playbackRate = 1;

            // Start playing
            if (!player.state.playing) {
                await player.play();
            }
        }

        // Start countdown
        preRollCountdown = preRoll;
        const countdownInterval = setInterval(() => {
            preRollCountdown--;
            if (preRollCountdown <= 0) {
                clearInterval(countdownInterval);
                preRollCountdown = null;
            }
        }, 1000);

        // Start recording after pre-roll
        preRollTimeout = setTimeout(async () => {
            try {
                isRecording = true;
                const recordingDuration = duration * 1000; // Convert to milliseconds
                await recordPlugin.startRecording({
                    deviceId: selectedDeviceId,
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false
                });
                // Stop recording when we reach the end time
                setTimeout(() => {
                    stopRecording();
                }, recordingDuration);

                // Start teleprompter auto-scroll
                startTeleprompterScroll();

                // Start countdown timer
                startCountdown();
            } catch (err) {
                console.error("Error starting recording:", err);
                toast.push(m.audio_recorder_start_failed({ error: err.message }), {
                    classes: ["toast-danger"],
                });
                isRecording = false;
                clearInterval(countdownInterval);
                preRollCountdown = null;
            }
        }, preRoll * 1000);
    } catch (err) {
        console.error("Error in startRecording:", err);
        toast.push(m.audio_recorder_start_failed({ error: err.message }), {
            classes: ["toast-danger"],
        });
        if (preRollTimeout) {
            clearTimeout(preRollTimeout);
            preRollTimeout = null;
        }
        preRollCountdown = null;
    }
}

function stopRecording() {
    try {
        if (recordPlugin && recordPlugin.isRecording()) {
            recordPlugin.stopRecording();
        }
        if (player && player.state.playing) {
            player.pause();
        }
        // Stop teleprompter auto-scroll
        stopTeleprompterScroll();
        // Stop countdown
        stopCountdown();
    } catch (err) {
        console.error("Error stopping recording:", err);
    }
}

function resetRecording() {
    recordedBlob = null;
    preRollCountdown = null;
    if (preRollTimeout) {
        clearTimeout(preRollTimeout);
        preRollTimeout = null;
    }
    if (wavesurfer) {
        wavesurfer.empty();
    }
    isRecording = false;
    // Reset teleprompter scroll
    stopTeleprompterScroll();
    if (teleprompterElement) {
        teleprompterElement.scrollTop = 0;
    }
    // Reset countdown
    stopCountdown();
    remainingTime = 0;

    startRecording();
}

function startCountdown() {
    if (countdownInterval) {
        clearInterval(countdownInterval);
    }

    // Set initial remaining time
    remainingTime = duration;

    // Update countdown every 100ms for smooth animation
    countdownInterval = setInterval(() => {
        remainingTime -= 0.1;
        if (remainingTime <= 0) {
            remainingTime = 0;
            stopCountdown();
        }
    }, 100);
}

function stopCountdown() {
    if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
    }
}

function startTeleprompterScroll() {
    if (!teleprompterElement) {
        return;
    }

    stopTeleprompterScroll();

    teleprompterElement.scrollTop = 0;
    let preciseScrollTop = 0; // Track sub-pixel scroll position
    lastScrollTime = performance.now();

    // Use rAF so the slider value (px/sec) maps to a consistent scroll velocity.
    const animate = (timestamp) => {
        const element = teleprompterElement;
        if (!element || !isRecording) {
            scrollAnimationFrame = null;
            return;
        }

        const deltaSeconds = Math.max(0, (timestamp - lastScrollTime) / 1000);
        lastScrollTime = timestamp;

        const speed = Number(effectiveScrollSpeed) || 0;
        const maxScroll = Math.max(
            0,
            element.scrollHeight - element.clientHeight,
        );

        if (maxScroll === 0 || speed <= 0) {
            scrollAnimationFrame = null;
            return;
        }

        // Calculate new position based on precise tracker
        preciseScrollTop += speed * Math.min(deltaSeconds, 0.2);

        if (preciseScrollTop >= maxScroll) {
            element.scrollTop = maxScroll;
            scrollAnimationFrame = null;
            return;
        }

        element.scrollTop = preciseScrollTop;
        scrollAnimationFrame = requestAnimationFrame(animate);
    };

    scrollAnimationFrame = requestAnimationFrame(animate);
}

function stopTeleprompterScroll() {
    if (scrollAnimationFrame) {
        cancelAnimationFrame(scrollAnimationFrame);
        scrollAnimationFrame = null;
    }
    lastScrollTime = null;
}

function increaseFontSize() {
    fontSize = Math.min(fontSize + 2, 72); // Max 72px
}

function decreaseFontSize() {
    fontSize = Math.max(fontSize - 2, 12); // Min 12px
}

async function previewRecording() {
    if (!recordedBlob) return;

    try {
        if (wavesurfer) {
            await wavesurfer.loadBlob(recordedBlob);
            wavesurfer.play();
        }
    } catch (err) {
        console.error("Error previewing recording:", err);
        toast.push(m.audio_recorder_preview_failed({ error: err.message }), {
            classes: ["toast-danger"],
        });
    }
}

async function saveRecording() {
    if (!recordedBlob || !event) return;

    isProcessing = true;
    uploadProgress = 0;

    try {
        if (!environment.online) {
            toast.push(m.audio_recorder_upload_online_only(), {
                classes: ["toast-warning"],
            });
            return;
        }

        const userId = auth.currentUser.uid;
        const recordingUuid = uuidv4();
        const fileName = `${recordingUuid}.mp3`;
        const storagePath = `users/${userId}/recordings/${fileName}`;

        // Convert blob to the correct format if needed
        const file = new File([recordedBlob], fileName, { type: "audio/mpeg" });

        // Upload to Firebase Storage
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
            "state_changed",
            (snapshot) => {
                uploadProgress =
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            },
            (error) => {
                console.error("Upload error:", error);
                toast.push(m.audio_recorder_upload_failed({ error: error.message }), {
                    classes: ["toast-danger"],
                });
                isProcessing = false;
            },
            async () => {
                try {
                    // Get the download URL using the cloud function
                    const getDownloadLink = httpsCallable(functions, "v8GetDownloadLink");
                    const result = await getDownloadLink(storagePath);

                    if (result && result.data && result.data[0]) {
                        await assignEventAudio(event, {
                            audioFile: result.data[0],
                            audioFileUuid: recordingUuid,
                            audioFileDuration: event.end - event.start,
                            audioFileRemote: storagePath,
                        });

                        // Store in history
                        insertHistoryAction({
                            name: "record audio",
                            eventGroup: projectState.selected,
                            snapshots: [
                                {
                                    store: "eventGroupState",
                                    value: JSON.stringify(eventGroupState),
                                },
                            ],
                        });

                        toast.push(m.audio_recorder_save_success(), {
                            classes: ["toast-success"],
                        });

                        hideModal();
                    } else {
                        throw new Error("Failed to get download URL");
                    }
                } catch (err) {
                    console.error("Error getting download URL:", err);
                    toast.push(m.audio_recorder_save_failed({ error: err.message }), {
                        classes: ["toast-danger"],
                    });
                } finally {
                    isProcessing = false;
                }
            },
        );
    } catch (err) {
        console.error("Error saving recording:", err);
        toast.push(m.audio_recorder_save_failed({ error: err.message }), {
            classes: ["toast-danger"],
        });
        isProcessing = false;
    }
}

function closeModal() {
    if (isRecording) {
        stopRecording();
    }
    cleanup();
    hideModal();
}

function handleMouseDown(e) {
    // Only start dragging if clicking on the header
    if (e.target.closest(".modal-header") && !e.target.closest(".btn-close")) {
        isDragging = true;
        dragStartX = e.clientX - modalX;
        dragStartY = e.clientY - modalY;

        // Change cursor for the entire document while dragging
        document.body.style.cursor = "move";
        document.body.style.userSelect = "none";
    }
}

function handleMouseMove(e) {
    if (!isDragging) return;

    e.preventDefault();
    modalX = e.clientX - dragStartX;
    modalY = e.clientY - dragStartY;
}

function handleMouseUp() {
    if (isDragging) {
        isDragging = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";

        // Save the current position to localStorage
        try {
            localStorage.setItem(
                "audioRecorderPosition",
                JSON.stringify({ x: modalX, y: modalY }),
            );
        } catch (err) {
            console.error("Error saving modal position:", err);
        }
    }
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'audioRecorder'
        ? 'show d-block'
        : ''}"
    role="dialog"
    aria-labelledby="audioRecorderTitle"
    tabindex="-1"
    id="AudioRecorderModal">
    <div
        bind:this={modalElement}
        id="audioRecorderDialog"
        class="modal-dialog modal-dialog-centered draggable-modal"
        class:is-dragging={isDragging}
        style="transform: translate({modalX}px, {modalY}px);"
        role="document">
        <div id="audioRecorderContent" class="modal-content">
            <div
                class="modal-header border-0 pb-0 draggable-header"
                role="button"
                tabindex="0"
                aria-label={m.audio_recorder_drag_label()}
                onmousedown={handleMouseDown}>
                <div class="d-flex align-items-center gap-3 w-100">
                    <div class="d-flex align-items-center gap-2">
                        <div class="bg-danger bg-opacity-10 rounded p-3">
                            <i class="bi bi-mic-fill fs-3 text-danger"></i>
                        </div>
                        <div>
                            <h4
                                class="modal-title mb-0"
                                id="audioRecorderTitle">
                                {m.audio_recorder_title()}
                            </h4>
                            <small class="text-muted"
                                >{m.audio_recorder_event_label({
                                    index: eventIndex !== null ? eventIndex + 1 : ""
                                })}</small>
                        </div>
                    </div>
                    <button
                        type="button"
                        class="btn-close ms-auto"
                        aria-label={m.audio_recorder_close_label()}
                        disabled={isRecording || isProcessing}
                        onclick={closeModal}></button>
                </div>
            </div>
            <div class="modal-body pt-3">
                {#if event}
                    <!-- Compact Event Timing and Pre-roll Row -->
                    <div class="card border-0 shadow-sm mb-3">
                        <div class="card-body py-2 px-3">
                            <div class="row g-3 align-items-base">
                                <div class="col-auto">
                                    <small class="text-muted d-block mb-1"
                                        >{m.audio_recorder_start_label()}</small>
                                    <span class="fw-semibold"
                                        >{event.start !== false
                                            ? event.start.toFixed(2) + "s"
                                            : m.audio_recorder_not_set()}</span>
                                </div>
                                <div class="col-auto">
                                    <small class="text-muted d-block mb-1"
                                        >{m.audio_recorder_end_label()}</small>
                                    <span class="fw-semibold"
                                        >{event.end !== false
                                            ? event.end.toFixed(2) + "s"
                                            : m.audio_recorder_not_set()}</span>
                                </div>
                                <div class="col-auto">
                                    <small class="text-muted d-block mb-1"
                                        >{m.audio_recorder_duration_label()}</small>
                                    <span class="fw-semibold"
                                        >{m.audio_recorder_duration_value({ seconds: duration.toFixed(2) })}</span>
                                </div>
                                <div class="col-auto">
                                    <small class="text-muted d-block mb-1"
                                        >{m.audio_recorder_pre_roll_label()}</small>
                                    <div
                                        class="d-flex align-items-center gap-3">
                                        <input
                                            id="preRollInput"
                                            type="number"
                                            class="form-control form-control-sm"
                                            bind:value={preRoll}
                                            min="0"
                                            max={maxPreRoll}
                                            step="0.5"
                                            style="width: 70px;"
                                            disabled={isRecording ||
                                                isProcessing} />
                                        <small class="text-muted text-nowrap"
                                            >{m.audio_recorder_pre_roll_max({
                                                seconds: maxPreRoll.toFixed(1)
                                            })}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Audio Device Selection -->
                    {#if audioDevices.length > 1}
                        <div class="card border-0 shadow-sm mb-3">
                            <div class="card-body py-2 px-3">
                                <div class="d-flex align-items-center gap-3">
                                    <label
                                        for="audioDeviceSelect"
                                        class="form-label mb-0 text-muted small">
                                        <i class="bi bi-mic me-1"></i>
                                    </label>
                                    <select
                                        id="audioDeviceSelect"
                                        class="form-select form-select-sm flex-grow-1"
                                        bind:value={selectedDeviceId}
                                        onchange={handleDeviceChange}
                                        disabled={isRecording || isProcessing}>
                                        {#each audioDevices as device, index (device.deviceId)}
                                            <option value={device.deviceId}>
                                                {device.label ||
                                                    m.audio_recorder_microphone_label({ index: index + 1 })}
                                            </option>
                                        {/each}
                                    </select>
                                </div>
                            </div>
                        </div>
                    {/if}

                    <!-- Validation Warnings -->
                    {#if duration < 2}
                        <div
                            class="alert alert-warning d-flex align-items-center mb-3 py-2"
                            role="alert">
                            <i class="bi bi-exclamation-triangle-fill me-2"></i>
                            <small
                                >{m.audio_recorder_duration_too_short()}</small>
                        </div>
                    {:else if duration > 30}
                        <div
                            class="alert alert-warning d-flex align-items-center mb-3 py-2"
                            role="alert">
                            <i class="bi bi-exclamation-triangle-fill me-2"></i>
                            <small
                                >{m.audio_recorder_duration_too_long()}</small>
                        </div>
                    {/if}

                    <!-- Teleprompter Display Card -->
                    {#if prompterText}
                        <div class="card border-0 shadow-sm mb-3">
                            <div
                                class="card-header bg-transparent border-0 py-2 px-3">
                                <div class="d-flex align-items-center gap-3">
                                    <!-- Font Size Controls -->
                                    <div
                                        class="d-flex align-items-center gap-2">
                                        <div
                                            class="btn-group btn-group-sm"
                                            role="group">
                                            <button
                                                type="button"
                                                class="btn btn-outline-secondary btn-sm px-2"
                                                onclick={decreaseFontSize}
                                                disabled={isRecording ||
                                                    isProcessing ||
                                                    fontSize <= 12}
                                                aria-label={m.audio_recorder_decrease_font_size_label()}
                                                style="min-width: 32px;">
                                                <i class="bi bi-dash-lg"></i>
                                            </button>
                                            <button
                                                type="button"
                                                class="btn btn-outline-secondary btn-sm px-2"
                                                onclick={increaseFontSize}
                                                disabled={isRecording ||
                                                    isProcessing ||
                                                    fontSize >= 72}
                                                aria-label={m.audio_recorder_increase_font_size_label()}
                                                style="min-width: 32px;">
                                                <i class="bi bi-plus-lg"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <!-- Scroll Speed Control -->
                                    <div
                                        class="d-flex align-items-center gap-2 flex-grow-1">
                                        <label class="d-flex align-items-center gap-1 mb-0">
                                            <input
                                                type="checkbox"
                                                class="form-check-input mt-0"
                                                bind:checked={autoScrollSpeed}
                                                disabled={isProcessing} />
                                            <small class="text-muted text-nowrap">{m.audio_recorder_auto_label()}</small>
                                        </label>
                                        <input
                                            type="range"
                                            class="form-range form-range-sm flex-grow-1"
                                            bind:value={scrollSpeed}
                                            min="10"
                                            max="150"
                                            step="5"
                                            disabled={isProcessing || autoScrollSpeed}
                                            aria-label={m.audio_recorder_scroll_speed_label()}
                                            style="max-width: 200px;" />
                                        <span
                                            class="badge bg-secondary text-nowrap small"
                                            style="min-width: 40px;"
                                            title={autoScrollSpeed ? m.audio_recorder_speed_auto_title() : m.audio_recorder_speed_manual_title()}
                                            >{effectiveScrollSpeed}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="card-body p-2">
                                <!-- Countdown Clock - Always visible -->
                                <div class="countdown-container">
                                    {#if isRecording && remainingTime > 0}
                                        <!-- Recording countdown (red with animated lines) -->
                                        <div
                                            class="countdown-line countdown-line-red"
                                            style="width: {countdownProgress *
                                                1.5}px;">
                                        </div>
                                        <div
                                            class="countdown-time countdown-time-red">
                                            {remainingTime.toFixed(1)}s
                                        </div>
                                        <div
                                            class="countdown-line countdown-line-red"
                                            style="width: {countdownProgress *
                                                1.5}px;">
                                        </div>
                                    {:else if preRollCountdown !== null}
                                        <!-- Pre-roll countdown (green, no lines) -->
                                        <div
                                            class="countdown-line"
                                            style="width: 0px;">
                                        </div>
                                        <div
                                            class="countdown-time countdown-time-green">
                                            {preRollCountdown}s
                                        </div>
                                        <div
                                            class="countdown-line"
                                            style="width: 0px;">
                                        </div>
                                    {:else}
                                        <!-- Default state (gray) -->
                                        <div
                                            class="countdown-line"
                                            style="width: 0px;">
                                        </div>
                                        <div
                                            class="countdown-time countdown-time-gray">
                                            {duration.toFixed(1)}s
                                        </div>
                                        <div
                                            class="countdown-line"
                                            style="width: 0px;">
                                        </div>
                                    {/if}
                                </div>

                                <div
                                    bind:this={teleprompterElement}
                                    class="teleprompter-content"
                                    class:is-scrolling={isRecording}
                                    style="font-size: {fontSize}px; font-family: {fontFamily};">
                                    {prompterText}
                                </div>
                            </div>
                        </div>
                    {/if}

                    <!-- Status Alerts -->
                    {#if preRollCountdown !== null}
                        <div
                            class="alert alert-info d-flex align-items-center justify-content-center mb-3 py-3"
                            role="alert">
                            <i class="bi bi-hourglass-split fs-4 me-3"></i>
                            <div>
                                <div class="fw-semibold">{m.audio_recorder_get_ready()}</div>
                                <div>
                                    {m.audio_recorder_recording_starts_in({
                                        seconds: preRollCountdown,
                                        suffix: preRollCountdown !== 1 ? "s" : ""
                                    })}
                                </div>
                            </div>
                        </div>
                    {/if}

                    {#if isRecording}
                        <div
                            class="alert alert-danger d-flex align-items-center justify-content-center mb-3 py-3"
                            role="alert">
                            <i
                                class="bi bi-record-circle-fill fs-4 me-3 recording-pulse"
                            ></i>
                            <div>
                                <div class="fw-bold">{m.audio_recorder_recording_in_progress()}</div>
                                <small
                                    >{m.audio_recorder_recording_auto_stop()}</small>
                            </div>
                        </div>
                    {/if}

                    {#if recordedBlob && !isProcessing}
                        <div
                            class="alert alert-success d-flex align-items-center mb-3 py-2"
                            role="alert">
                            <i class="bi bi-check-circle-fill me-2"></i>
                            <small
                                >{m.audio_recorder_recording_complete()}</small>
                        </div>
                    {/if}

                    <!-- Waveform Display Card -->
                    <div class="card border-0 shadow-sm mb-3">
                        <div class="card-body p-0">
                            <div
                                id="audioRecorderWaveform"
                                class="waveform-container">
                            </div>
                        </div>
                    </div>

                    <!-- Upload Progress -->
                    {#if isProcessing && uploadProgress > 0}
                        <div class="card border-0 shadow-sm mb-3">
                            <div class="card-body py-2 px-3">
                                <div
                                    class="d-flex align-items-center justify-content-between mb-2">
                                    <small class="fw-semibold">
                                        <i class="bi bi-cloud-upload me-2"></i
                                        >{m.audio_recorder_saving_recording()}
                                    </small>
                                    <span class="badge bg-primary"
                                        >{uploadProgress.toFixed(0)}%</span>
                                </div>
                                <div
                                    class="progress"
                                    role="progressbar"
                                    style="height: 6px;">
                                    <div
                                        class="progress-bar progress-bar-striped progress-bar-animated"
                                        style="width: {uploadProgress}%">
                                    </div>
                                </div>
                            </div>
                        </div>
                    {/if}
                {:else}
                    <div
                        class="alert alert-warning d-flex align-items-center"
                        role="alert">
                        <i class="bi bi-exclamation-triangle-fill fs-5 me-2"
                        ></i>
                        <div>
                            {m.audio_recorder_no_event_selected()}
                        </div>
                    </div>
                {/if}
            </div>
            <div
                class="modal-footer border-0 pt-0 d-flex justify-content-between">
                <!-- Left: Control Buttons -->
                <div class="btn-group shadow-sm" role="group">
                    {#if recordedBlob === null}
                        <button
                            type="button"
                            aria-label={m.audio_recorder_start_label_button()}
                            class="btn btn-lg btn-danger"
                            disabled={!canRecord ||
                                recordedBlob !== null ||
                                isRecording ||
                                isProcessing}
                            onclick={startRecording}>
                            <i class="bi bi-record-circle"></i>
                        </button>
                    {:else}
                        <button
                            type="button"
                            aria-label={m.audio_recorder_reset_label_button()}
                            class="btn btn-lg btn-outline-warning"
                            disabled={!recordedBlob ||
                                isRecording ||
                                isProcessing}
                            onclick={resetRecording}>
                            <i class="bi bi-arrow-clockwise"></i>
                        </button>
                    {/if}
                    <button
                        type="button"
                        aria-label={m.audio_recorder_preview_label_button()}
                        class="btn btn-lg btn-outline-primary"
                        disabled={!recordedBlob || isRecording || isProcessing}
                        onclick={previewRecording}>
                        <i class="bi bi-play-circle"></i>
                    </button>
                </div>
                <!-- Right: Save Button -->
                <button
                    type="button"
                    class="btn btn-lg btn-success shadow-sm"
                    disabled={!recordedBlob || isRecording || isProcessing}
                    onclick={saveRecording}>
                    <i class="bi bi-check-lg"></i> {m.audio_recorder_save_button()}
                </button>
            </div>
        </div>
    </div>
</div>

<style>
.waveform-container {
    min-height: 120px;
    background: linear-gradient(
        135deg,
        rgba(15, 188, 140, 0.05) 0%,
        rgba(13, 233, 170, 0.05) 100%
    );
    border-radius: 0.375rem;
    padding: 1rem;
}

.recording-pulse {
    animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.7;
        transform: scale(1.1);
    }
}

/* Ensure progress bar has proper height */
.progress {
    border-radius: 0.5rem;
    overflow: hidden;
}

/* Card hover effect for interactive elements */
.card {
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

/* Button group styling */
.btn-group .btn {
    min-width: 120px;
}

/* Badge styling */
.badge {
    font-weight: 500;
}

/* Countdown Clock Styles */
.countdown-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    margin-bottom: 0.5rem;
}

.countdown-time {
    font-size: 1.5rem;
    font-weight: bold;
    min-width: 80px;
    text-align: center;
    font-variant-numeric: tabular-nums;
    transition: color 0.3s ease;
}

.countdown-time-red {
    color: #dc3545;
}

.countdown-time-green {
    color: #198754;
}

.countdown-time-gray {
    color: #6c757d;
}

.countdown-line {
    height: 3px;
    background: transparent;
    border-radius: 2px;
    transition: width 0.25s linear;
    width: 150px;
}

.countdown-line-red {
    background: linear-gradient(90deg, #dc3545 0%, #ff6b6b 100%);
}

/* Teleprompter styles */
.teleprompter-content {
    height: 200px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1rem;
    background: linear-gradient(
        135deg,
        rgba(0, 0, 0, 0.02) 0%,
        rgba(0, 0, 0, 0.05) 100%
    );
    border-radius: 0.375rem;
    line-height: 1.6;
    text-align: left;
    white-space: pre-wrap;
    word-wrap: break-word;
    position: relative;
    resize: vertical;
}

.teleprompter-content.is-scrolling {
    user-select: none;
}

.teleprompter-content::-webkit-scrollbar {
    width: 8px;
}

.teleprompter-content::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
}

.teleprompter-content::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.teleprompter-content::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

.form-range-sm {
    height: 0.5rem;
}

/* Draggable modal styles */
.draggable-modal {
    transition: none !important;
    position: fixed;
    margin: 0;
}

.draggable-modal.is-dragging {
    transition: none !important;
}

.draggable-header {
    cursor: move;
    user-select: none;
}

.draggable-header:active {
    cursor: move;
}

/* Prevent text selection while dragging */
.draggable-header * {
    pointer-events: none;
}

.draggable-header .btn-close {
    pointer-events: auto;
}
</style>
